import translation from 'locales/translation';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import Breadcrumb, {
  IBreadCrumbItem,
} from 'components/__ASSESSOR__/BreadCrumb/BreadCrumb';
import PrivateContainer from 'components/__SHARED__/PrivateContainer';
import ApiService from 'services/ApiService';

import {
  AssessorCadastroEventoRoutePath,
  AssessorCadastroReuniaoRoutePath,
  AssessorRoutePath,
} from 'library/Routes';

import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  Col,
  ComunidadeIcon,
  EditIcon,
  Etapas,
  EvidenciasIcon,
  ExclamationIcon,
  LocalIcon,
  Row,
  Title,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import { Attachment, Data, Entities, Local, Staves, Survey } from './Etapas';

import { AxiosError, AxiosResponse } from 'axios';
import { AlertIcon, SearchIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import { AuthContext } from 'context/authContext/AuthContext';
import { RegisterContextActions } from 'context/registerContext/RegisterContextActions';
import {
  AssemblyModalities,
  AssemblyModalitiesTemplate,
  AssemblyType,
  SituationText,
  SituationValues,
  StepIds,
  StepStatus,
  Toggle,
} from 'helpers/enums/Assembleia';
import { getProfileByJWTRoles } from 'helpers/utils/AuthUtils';
import { getAssemblySteps } from 'helpers/utils/CreateEventUtils';
import {
  clsx,
  isNullOrUndefined,
  returnDateAndHour,
} from 'helpers/utils/HTMLUtils';
import {
  getParsedJWTAssessorAccessToken,
  JWTAssessorJson,
} from 'helpers/utils/JWTUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { toRequest } from 'helpers/utils/RequestUtils';
import { capitalize, onlyNumbers } from 'helpers/utils/StringUtils';
import { useRegisterContext } from 'hooks/useRegisterContext';
import { getAssembleiasAssessorBffEndpoint } from 'library/Apis';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAgencies,
  getAgendaRegister,
  getAssemblyType,
  getCooperative,
  getNucleuses,
  getTemplates,
  postMakeEditable,
} from 'services/assessor';
import { assembleiasAssessorLoadEventAttachment } from 'store/modules/AssembleiasAssessorActions';
import {
  AuthUserPerfilId,
  IAuthUserPerfil,
} from 'store/modules/auth/IAuthUserPerfil';
import { IReducer } from 'store/Reducer';
import { getNucleusByKey } from './Etapas/Entities/Entities';
import styles from './styles.module.scss';

export const makeEditable = (
  eventId: string,
  callbackSuccess: (resp: AxiosResponse) => void,
  callbackError: (err: AxiosError) => void
) => {
  postMakeEditable(Number(eventId))
    .then((resp: AxiosResponse) => {
      callbackSuccess(resp);
    })
    .catch((error: AxiosError) => {
      callbackError(error);
    });
};

export type GetDataProps = {
  eventId: number;
  status: string;
  convocationStatus: string;
  data: {
    title: string;
    cooperative: string;
    type: AssemblyType | undefined;
    modality: AssemblyModalities | undefined;
    description: string | undefined;
    link: string;
    quantityAssociates: string | number | null;
    microsoftTeams: Toggle | undefined;
    startDate: string;
    startHour: string;
    endDate: string;
    endHour: string;
    flgEventTest: boolean;
    hybridStartDate: string;
    hybridStartHour: string;
    hybridEndDate: string;
    hybridEndHour: string;
    tmpQuantityAssociates: string | number | null;
    tmpStartDate: string;
    tmpStartHour: string;
    inviteAssociate?: boolean;
    inviteCoordinator?: boolean;
  };
  nucleuses: {
    nucleusesQuantity: number;
    nucleuses: {
      [index: number]: [
        {
          id: number;
          cooperative: string;
          branch: string;
          code: string;
        }
      ];
    };
  };
  local: {
    ldap: string;
    authorization: string;
    eventId: number;
    postcode: string;
    state: string;
    city: string;
    neighborhood: string;
    address: string;
    number: string;
    localReference: string;
  };
  survey: boolean;
  templateId: string | null;
};

interface Step {
  step: StepIds;
  completed: boolean;
}

export const previousStepIsCompleted = (
  completedSteps: Step[],
  targetStep: string,
  ignoredSteps: string[] = []
): boolean => {
  const targetIndex = completedSteps.findIndex(
    (step) => step.step === targetStep
  );

  if (targetIndex <= 0) {
    return false;
  }

  const previousStep = completedSteps[targetIndex - 1];

  const ignoredStepsArray = Array.isArray(ignoredSteps) ? ignoredSteps : [];

  if (ignoredStepsArray.includes(previousStep.step)) {
    const prevIndex = completedSteps.findIndex(
      (step) => step.step === previousStep.step
    );

    if (prevIndex <= 0) {
      return false;
    }

    return completedSteps[prevIndex - 1].completed;
  }

  return previousStep.completed;
};

export default function Cadastro(props: { reu?: boolean }) {
  const [opacity, setOpacity] = useState<boolean>(true);
  const [dataGet, setDataGet] = useState<GetDataProps>();
  const [loader, setLoader] = useState<boolean>(false);

  const history = useHistory();
  const location = useLocation();
  const { showToast } = useContext(LayoutContext);
  const dispatchAction = useDispatch();
  const match = useRouteMatch<{ id?: string; etapa?: string }>();
  const { auth } = useSelector((reducer: IReducer) => reducer);
  const { user } = useContext(AuthContext);

  const parsedToken: JWTAssessorJson | null = getParsedJWTAssessorAccessToken(
    user!.access_token
  );

  const perfil: IAuthUserPerfil | null = parsedToken
    ? getProfileByJWTRoles(parsedToken.roles)
    : null;

  const breadcrumb = useMemo(
    (): IBreadCrumbItem[] => [
      { nome: translation.MainRoute.title, url: AssessorRoutePath },
      {
        nome: translation.Assessor.Evento.Cadastro.Titulo(props.reu),
      },
    ],
    [props.reu]
  );

  const {
    state,
    setStepCompleted,
    setStepStatus,
    changeState,
    dataStepSetFilter,
    dataStepSetFilterOld,
    localStepSetFilter,
    localStepSetFilterOld,
    surveyStepSetFilter,
    entitiesStepSetFilter,
  } = useRegisterContext();

  const shouldRenderReuOrAssembly = useMemo(
    () =>
      props.reu
        ? AssessorCadastroReuniaoRoutePath
        : AssessorCadastroEventoRoutePath,
    [props.reu]
  );

  const url = `${getAssembleiasAssessorBffEndpoint('1')}event/with-steps/${
    match.params.id
  }`;

  const completedData = state.completed.find(
    (item: { step: StepIds }) => item.step === StepIds.data
  )?.completed;

  const getNonNullKeys = (obj: any, additionalKeys?: string[]): string[] => {
    const mappedKeys: { [key: string]: string } = {
      data: 'dados',
      nucleuses: 'entidade',
      local: 'local',
    };

    const keysToReturn: string[] = [];

    if (obj.data && obj.data.title !== null) {
      keysToReturn.push(mappedKeys.data);
    }

    if (obj.nucleuses && obj.nucleuses.nucleusesQuantity > 0) {
      keysToReturn.push(mappedKeys.nucleuses);
    }

    if (obj.local && obj.local.state !== null) {
      keysToReturn.push(mappedKeys.local);
    }

    if (additionalKeys) {
      additionalKeys.forEach((key) => {
        if (mappedKeys[key]) {
          if (obj[key] !== null) {
            keysToReturn.push(mappedKeys[key]);
          }
        } else if (obj[key] !== null) {
          keysToReturn.push(key);
        }
      });
    }

    return keysToReturn;
  };

  // INITIAL STEP RULE

  const presentialBlocked = useMemo(() => {
    return (
      state.data.campos.modality === AssemblyModalities.OFFLINE &&
      state.status === StepStatus.VISUALIZACAO &&
      state.eventStatus === SituationValues.BLOCKED
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.modality]);

  const isDigital = [
    AssemblyModalities.ONLINE,
    AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
  ].includes(state.data.campos.modality!);

  const isAgnType = useMemo(() => {
    return state.data.campos.type! === AssemblyType.AGN && !props.reu;
  }, [props.reu, state.data.campos.type]);

  const isReservable = useMemo(() => {
    return (
      state.eventStatus === SituationValues.RESERVABLE &&
      [StepStatus.VISUALIZACAO, StepStatus.EM_EDICAO].includes(state.status)
    );
  }, [state.eventStatus, state.status]);

  const getStepCompleted = (step: StepIds) => {
    return isReservable
      ? false
      : state.completed.filter!(
          (x: { step: StepIds; completed: boolean }) => x.step === step
        )[0].completed;
  };

  const areRequiredStepsCompleted = useCallback(
    (steps: Step[]): boolean => {
      const requiredSteps: string[] = isDigital
        ? ['dados', 'entidade']
        : ['dados', 'entidade', 'local'];

      return requiredSteps.every((requiredStep) => {
        const stepObject = steps.find((step) => step.step === requiredStep);
        return stepObject && stepObject.completed;
      });
    },
    [isDigital]
  );

  const getStepDisabledCompleted = (etapa: StepIds) => {
    const getIcon = (icon: React.ReactNode) => ({ icon });

    const alwaysDisable = isReservable || state.status === StepStatus.CRIACAO;

    const isStepDisabled = (targetStep: StepIds, ignoreStep?: StepIds[]) => {
      if (alwaysDisable || presentialBlocked) {
        return true;
      }

      if (targetStep) {
        if (state.status !== StepStatus.VISUALIZACAO) {
          const allDependenciesCompleted = state.completed.every(() =>
            previousStepIsCompleted(state.completed, targetStep, ignoreStep)
          );

          return !allDependenciesCompleted;
        }
      }

      return false;
    };

    switch (etapa) {
      case StepIds.data:
        return {
          ...getIcon(<CalendarIcon />),
          completed: isReservable || getStepCompleted(StepIds.data),
          disabled: presentialBlocked,
        };
      case StepIds.entity:
        return {
          ...getIcon(<ComunidadeIcon />),
          completed: getStepCompleted(StepIds.entity),
          disabled: isStepDisabled(StepIds.entity),
        };
      case StepIds.local:
        return {
          ...getIcon(<LocalIcon />),
          completed: getStepCompleted(StepIds.local),
          disabled: isStepDisabled(StepIds.local),
        };
      case StepIds.staves:
        return {
          ...getIcon(<ExclamationIcon />),
          completed: areRequiredStepsCompleted(state.completed)
            ? getStepCompleted(StepIds.staves)
            : false,
          disabled: isStepDisabled(
            StepIds.staves,
            isDigital ? [StepIds.local] : []
          ),
        };
      case StepIds.survey:
        return {
          ...getIcon(<SearchIcon />),
          completed:
            areRequiredStepsCompleted(state.completed) &&
            !isReservable &&
            previousStepIsCompleted(state.completed, StepIds.survey) &&
            getStepCompleted(StepIds.survey),
          disabled:
            alwaysDisable ||
            (state.status === StepStatus.EM_EDICAO &&
              (isDigital
                ? areRequiredStepsCompleted(state.completed)
                  ? !previousStepIsCompleted(state.completed, StepIds.survey) ||
                    props.reu
                    ? isNullOrUndefined(state.survey.enabled)
                    : false
                  : true
                : isNullOrUndefined(state.initialStatus) ||
                  state.eventStatus === state.initialStatus
                ? areRequiredStepsCompleted(state.completed) &&
                  !previousStepIsCompleted(state.completed, StepIds.survey)
                  ? true
                  : !areRequiredStepsCompleted(state.completed)
                : areRequiredStepsCompleted(state.completed) &&
                  previousStepIsCompleted(state.completed, StepIds.survey)
                ? false
                : true)),
        };

      case StepIds.attachment:
        return {
          ...getIcon(<EvidenciasIcon />),
          completed: areRequiredStepsCompleted(state.completed)
            ? presentialBlocked &&
              previousStepIsCompleted(
                state.completed,
                StepIds.attachment,
                isDigital ? [] : [StepIds.survey]
              )
              ? true
              : getStepCompleted(StepIds.entity) &&
                getStepCompleted(StepIds.attachment)
            : false,
          disabled:
            alwaysDisable ||
            (state.status !== StepStatus.VISUALIZACAO &&
              (isDigital
                ? !getStepCompleted(StepIds.entity) ||
                  !previousStepIsCompleted(
                    state.completed,
                    StepIds.attachment,
                    isAgnType
                      ? isDigital
                        ? []
                        : [StepIds.survey]
                      : [StepIds.survey]
                  )
                : isNullOrUndefined(state.initialStatus) ||
                  state.eventStatus === state.initialStatus
                ? areRequiredStepsCompleted(state.completed) &&
                  !previousStepIsCompleted(
                    state.completed,
                    StepIds.attachment,
                    isAgnType ? [] : [StepIds.survey]
                  )
                  ? true
                  : !areRequiredStepsCompleted(state.completed)
                : areRequiredStepsCompleted(state.completed) &&
                  previousStepIsCompleted(state.completed, StepIds.attachment)
                ? false
                : true)),
        };
      default:
        return {};
    }
  };

  useEffect(() => {
    if (state.data.campos.cooperative) {
      if (!completedData) {
        changeState(RegisterContextActions.ENTITIES_AGENCY_LOAD, true);
      }

      getAgencies(state.data.campos.cooperative)
        .then((agencies) => {
          changeState(
            RegisterContextActions.ENTITIES_AGENCY,
            agencies.data.map(
              (agn: { id: string; codigo: string; nome: string }) => {
                return {
                  label: `${agn.codigo} - ${agn.nome}`,
                  value: agn.codigo,
                };
              }
            )
          );
        })
        .catch(() => {
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Error,
            showStatusBar: true,
            timeout: 4000,
            label: translation.Toasts.Errors.loadAgency,
          });
        })
        .finally(() => {
          changeState(RegisterContextActions.ENTITIES_AGENCY_LOAD, false);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.cooperative]);

  useLayoutEffect(() => {
    if (state.eventStatus === null) {
      if (
        match.params.id &&
        match.params.id.length === 5 &&
        ((match.params.id && match.params.etapa) ||
          (match.params.id && String(match.params.id).match(/^\d+$/)))
      ) {
        if (
          [StepStatus.VISUALIZACAO, StepStatus.EM_EDICAO].includes(state.status)
        ) {
          setLoader(true);
          toRequest(ApiService.get, [url])
            .then((infos) => {
              changeState(
                RegisterContextActions.SET_EVENT_STATUS,
                infos.data.status
              );
              changeState(
                RegisterContextActions.SET_INITIAL_STATUS,
                infos.data.status
              );
              setDataGet(infos.data);
            })
            .catch((error) => {
              if (
                error.response.status === 417 &&
                error.response.data.message === 'event.isDeleted'
              ) {
                showToast({
                  pauseOnFocusLoss: true,
                  theme: ToastTypes.Error,
                  showStatusBar: true,
                  timeout: 4000,
                  label:
                    translation.Toasts.Errors.Keys[
                      `${
                        (error as AxiosError<Error>).response?.data.message ||
                        (error as AxiosError<Error>).response?.data ||
                        (error as AxiosError<Error>).message
                      }` as keyof typeof translation.Toasts.Errors.Keys
                    ] ?? translation.Toasts.Errors.defaultAlternative,
                });
                history.push(AssessorRoutePath);
              } else {
                showToast({
                  pauseOnFocusLoss: true,
                  theme: ToastTypes.Error,
                  showStatusBar: true,
                  timeout: 4000,
                  label:
                    translation.Toasts.Errors.Keys[
                      `${
                        (error as AxiosError<Error>).response?.data.message ||
                        (error as AxiosError<Error>).response?.data ||
                        (error as AxiosError<Error>).message
                      }` as keyof typeof translation.Toasts.Errors.Keys
                    ] ?? translation.Toasts.Errors.defaultAlternative,
                });
              }
            })
            .finally(() =>
              setTimeout(() => {
                setLoader(false);
              }, 500)
            );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  useEffect(() => {
    if (!completedData) {
      changeState(RegisterContextActions.DATA_ASSEMBLY_COOPERATIVE_LOAD, true);
      getCooperative()
        .then((coops) => {
          changeState(
            RegisterContextActions.DATA_ASSEMBLY_COOPERATIVE,
            coops.data.map((items: { codigo: string; nome: string }) => {
              return {
                label: `${items.codigo} - ${items.nome}`,
                value: items.codigo,
              };
            })
          );
        })
        .catch(() => {
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Error,
            showStatusBar: true,
            timeout: 4000,
            label: translation.Toasts.Errors.loadCooperative,
          });
        })
        .finally(() => {
          changeState(
            RegisterContextActions.DATA_ASSEMBLY_COOPERATIVE_LOAD,
            false
          );
        });
    }

    changeState(RegisterContextActions.DATA_ASSEMBLY_TYPE_LOAD, true);
    changeState(RegisterContextActions.DATA_ASSEMBLY_MODALITY_LOAD, true);
    changeState(RegisterContextActions.DATA_ASSEMBLY_LINK_LOAD, true);

    getAssemblyType()
      .then((assembly) => {
        changeState(
          RegisterContextActions.DATA_ASSEMBLY_TYPE,
          assembly.data.types
        );
        changeState(
          RegisterContextActions.DATA_ASSEMBLY_MODALITY,
          assembly.data.modalities
        );
        changeState(
          RegisterContextActions.DATA_ASSEMBLY_LINK,
          assembly.data.link
        );
        changeState(
          RegisterContextActions.SET_MAXIMUM_QUANTITY_ASSOCIATES,
          assembly.data.maximumQuantityAssociates
        );
      })
      .catch(() => {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: translation.Toasts.Errors.loadInfoCreateEvent,
        });
      })
      .finally(() => {
        changeState(RegisterContextActions.DATA_ASSEMBLY_TYPE_LOAD, false);
        changeState(RegisterContextActions.DATA_ASSEMBLY_MODALITY_LOAD, false);
        changeState(RegisterContextActions.DATA_ASSEMBLY_LINK_LOAD, false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalidadeTemplate = useCallback(
    (modalidade: AssemblyModalities) => {
      switch (modalidade) {
        case AssemblyModalities.ONLINE:
          return AssemblyModalitiesTemplate.ONLINE;
        case AssemblyModalities.OFFLINE:
          return AssemblyModalitiesTemplate.OFFLINE;
        case AssemblyModalities.HIBRIDO_SIMULTANEO:
          return AssemblyModalitiesTemplate.HIBRIDO_SIMULTANEO;
        case AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA:
          return AssemblyModalitiesTemplate.HIBRIDO_DIGITAL_AGENCIA;
        case AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL:
          return AssemblyModalitiesTemplate.HIBRIDO_PRESENCIAL_DIGITAL;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.data.campos.modality]
  );

  // Get templates
  useEffect(() => {
    if (
      state?.data?.campos?.cooperative?.length &&
      state?.data?.campos?.type === AssemblyType.AGN &&
      state?.data?.campos?.modality
    ) {
      changeState(RegisterContextActions.SURVEY_TEMPLATES_LOAD, true);
      getTemplates(
        state.data.campos.cooperative,
        modalidadeTemplate(state.data.campos.modality as any) as any
      )
        .then((template) => {
          changeState(RegisterContextActions.SURVEY_TEMPLATES, template.data);
          // surveyStepSetFilter({ templatesOld: template.data });
        })
        .catch(() => {
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Error,
            showStatusBar: true,
            timeout: 4000,
            label: translation.Toasts.Errors.loadTemplates,
          });
        })
        .finally(() => {
          changeState(RegisterContextActions.SURVEY_TEMPLATES_LOAD, false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.data.campos.type,
    state.data.campos.cooperative,
    state.data.campos.modality,
  ]);

  useEffect(() => {
    if (
      match.params.id &&
      match.params.id.length === 5 &&
      ((match.params.id && match.params.etapa) ||
        (match.params.id && String(match.params.id).match(/^\d+$/)))
    ) {
      setStepStatus(
        state.eventStatus === SituationValues.EDITABLE
          ? StepStatus.EM_EDICAO
          : StepStatus.VISUALIZACAO
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.eventStatus]);

  useEffect(() => {
    if (dataGet) {
      if (state.eventStatus !== null) {
        if (!state.data.campos.title.length) {
          // Data step

          const formatedStartDate = dataGet.data.startDate
            ? returnDateAndHour(dataGet.data.startDate)
            : { formatedDate: '', formatedHour: '' };
          const formatedEndDate = dataGet.data.endDate
            ? returnDateAndHour(dataGet.data.endDate)
            : { formatedDate: '', formatedHour: '' };
          const formatedHybridStartDate = returnDateAndHour(
            dataGet.data.hybridStartDate
          );
          const formatedHybridEndDate = returnDateAndHour(
            dataGet.data.hybridEndDate
          );

          dataStepSetFilter({
            title: dataGet.data.title,
            cooperative: dataGet.data.cooperative,
            description: dataGet.data.description,
            startDate: formatedStartDate.formatedDate,
            endDate: formatedEndDate.formatedDate,
            hybridStartDate:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridStartDate === null
                ? formatedStartDate.formatedDate
                : formatedHybridStartDate.formatedDate,
            hybridEndDate:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridEndDate === null
                ? formatedEndDate.formatedDate
                : formatedHybridEndDate.formatedDate,
            hybridEndHour:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridStartDate === null
                ? formatedStartDate.formatedHour
                : formatedHybridEndDate.formatedHour,
            hybridStartHour:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridEndDate === null
                ? formatedEndDate.formatedHour
                : formatedHybridStartDate.formatedHour,
            link: dataGet.data.link ?? '',
            flgEventTest: dataGet.data.flgEventTest ?? false,
            modality: dataGet.data.modality,
            startHour: formatedStartDate.formatedHour,
            endHour: formatedEndDate.formatedHour,
            type: dataGet.data.type,
            quantityAssociates: dataGet.data.quantityAssociates,
            microsoftTeams:
              isNullOrUndefined(dataGet.data.microsoftTeams) &&
              dataGet.data.modality === AssemblyModalities.OFFLINE
                ? Toggle.DISABLED
                : dataGet.data.microsoftTeams,
            tmpQuantityAssociates: dataGet.data.quantityAssociates,
            flagAssociate: props.reu ? dataGet.data.inviteAssociate! : true,
            flagCoordinator: props.reu ? dataGet.data.inviteCoordinator! : true,
            tmpStartDate: [
              AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              AssemblyModalities.HIBRIDO_SIMULTANEO,
              AssemblyModalities.ONLINE,
            ].includes(dataGet.data.modality!)
              ? formatedStartDate.formatedDate
              : [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                  dataGet.data.modality!
                )
              ? formatedHybridStartDate.formatedDate
              : formatedStartDate.formatedDate,
            tmpStartHour: [
              AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              AssemblyModalities.HIBRIDO_SIMULTANEO,
              AssemblyModalities.ONLINE,
            ].includes(dataGet.data.modality!)
              ? formatedStartDate.formatedHour
              : [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                  dataGet.data.modality!
                )
              ? formatedHybridStartDate.formatedHour
              : formatedStartDate.formatedHour,
          });

          dataStepSetFilterOld({
            title: dataGet.data.title,
            cooperative: dataGet.data.cooperative,
            description: dataGet.data.description,
            startDate: formatedStartDate.formatedDate,
            endDate: formatedEndDate.formatedDate,
            hybridStartDate:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridStartDate === null
                ? formatedStartDate.formatedDate
                : formatedHybridStartDate.formatedDate,
            hybridEndDate:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridEndDate === null
                ? formatedEndDate.formatedDate
                : formatedHybridEndDate.formatedDate,
            hybridEndHour:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridStartDate === null
                ? formatedStartDate.formatedHour
                : formatedHybridEndDate.formatedHour,
            hybridStartHour:
              [
                AssemblyModalities.HIBRIDO_SIMULTANEO,
                AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              ].includes(dataGet.data.modality!) &&
              dataGet.data.hybridEndDate === null
                ? formatedEndDate.formatedHour
                : formatedHybridStartDate.formatedHour,
            link: dataGet.data.link ?? '',
            flgEventTest: dataGet.data.flgEventTest ?? false,
            modality: dataGet.data.modality,
            startHour: formatedStartDate.formatedHour,
            endHour: formatedEndDate.formatedHour,
            type: dataGet.data.type,
            quantityAssociates: dataGet.data.quantityAssociates,
            microsoftTeams:
              isNullOrUndefined(dataGet.data.microsoftTeams) &&
              dataGet.data.modality === AssemblyModalities.OFFLINE
                ? Toggle.DISABLED
                : dataGet.data.microsoftTeams,
            tmpQuantityAssociates: dataGet.data.quantityAssociates,
            flagAssociate: props.reu ? dataGet.data.inviteAssociate! : true,
            flagCoordinator: props.reu ? dataGet.data.inviteCoordinator! : true,
            tmpStartDate: [
              AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              AssemblyModalities.HIBRIDO_SIMULTANEO,
              AssemblyModalities.ONLINE,
            ].includes(dataGet.data.modality!)
              ? formatedStartDate.formatedDate
              : [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                  dataGet.data.modality!
                )
              ? formatedHybridStartDate.formatedDate
              : formatedStartDate.formatedDate,
            tmpStartHour: [
              AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
              AssemblyModalities.HIBRIDO_SIMULTANEO,
              AssemblyModalities.ONLINE,
            ].includes(dataGet.data.modality!)
              ? formatedStartDate.formatedHour
              : [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                  dataGet.data.modality!
                )
              ? formatedHybridStartDate.formatedHour
              : formatedStartDate.formatedHour,
          });
        }

        // Entitie step

        if (dataGet.nucleuses !== null) {
          if (state.data.cooperativeOptions.length) {
            entitiesStepSetFilter({
              agency: [],
              nucleou: [],
            });
          }

          if (state.entities.agencyOptions.length) {
            const resultArray = Object.entries(dataGet.nucleuses.nucleuses).map(
              ([agn, nucArray]: any[]) => {
                return {
                  id: `id-${getUniqueKey()}`,
                  agn: state.entities.agencyOptions.find(
                    (x: any) => x.value === agn
                  ),

                  nuc: nucArray.map(
                    (nuc: {
                      id: number;
                      cooperative: string;
                      branch: string;
                      code: string;
                    }) => ({ ...nuc })
                  ),
                };
              }
            );

            changeState(
              RegisterContextActions.ENTITIES_TABLE_DATA,
              resultArray
            );

            changeState(
              RegisterContextActions.ENTITIES_TABLE_DATA_OLD,
              resultArray
            );
          }
        }

        // Local step

        if (dataGet.local !== null) {
          localStepSetFilter({
            state: dataGet.local.state ?? '',
            city: dataGet.local.city ?? '',
            neighborhood: dataGet.local.neighborhood ?? '',
            address: dataGet.local.address ?? '',
            postcode: onlyNumbers(dataGet.local.postcode) ?? '',
            number: dataGet.local.number ?? '',
            localReference: dataGet.local.localReference ?? '',
          });
          localStepSetFilterOld({
            state: dataGet.local.state ?? '',
            city: dataGet.local.city ?? '',
            neighborhood: dataGet.local.neighborhood ?? '',
            address: dataGet.local.address ?? '',
            postcode: onlyNumbers(dataGet.local.postcode) ?? '',
            number: dataGet.local.number ?? '',
            localReference: dataGet.local.localReference ?? '',
          });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataGet,
    props.reu,
    state.data.cooperativeOptions,
    state.entities.agencyOptions.length,
    state.eventStatus,
  ]);

  useEffect(() => {
    if (dataGet) {
      const nonNullKeys = getNonNullKeys(
        dataGet,
        isReservable
          ? []
          : isAgnType
          ? ['anexo', 'pesquisa', 'pauta']
          : ['anexo', 'pauta']
      );

      const updatedState = state.completed.map((item) =>
        nonNullKeys.includes(item.step) ? { ...item, completed: true } : item
      );

      if (dataGet.survey !== null) {
        surveyStepSetFilter({
          enabled: dataGet.survey,
          enabledOld: dataGet.survey,
          template: dataGet.templateId,
          templateOld: dataGet.templateId,
        });
      }

      setStepCompleted(updatedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader]);

  useEffect(() => {
    if (!props.reu) {
      if (presentialBlocked || state.status === StepStatus.EM_EDICAO) {
        const completedRule = (additionalRule: boolean) => {
          if (isNullOrUndefined(state.initialStatus)) {
            return false;
          } else {
            if (state.initialStatus === SituationValues.RESERVABLE) {
              return dataGet && state.eventStatus === state.initialStatus
                ? true
                : Boolean(
                    dataGet &&
                      state.eventStatus === state.initialStatus &&
                      additionalRule
                  );
            }

            if (dataGet) {
              const nonNullKeys = getNonNullKeys(dataGet);

              if (isDigital) {
                if (nonNullKeys.length < 2) {
                  return false;
                }
              } else {
                if (nonNullKeys.length < 3) {
                  return false;
                }
              }
            }
          }

          return true;
        };

        const updatedState = state.completed.map((item) => {
          if (item.step === 'anexo') {
            return {
              ...item,
              completed: completedRule(Boolean(state.attachments.files.length)),
            };
          } else if (item.step === 'pesquisa') {
            return {
              ...item,
              completed: completedRule(
                !isNullOrUndefined(state.survey.enabled)
              ),
            };
          } else if (item.step === 'pauta') {
            return {
              ...item,
              completed: completedRule(Boolean(state.staves.agendas.length)),
            };
          } else {
            return item;
          }
        });

        setStepCompleted(updatedState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.attachments.files, state.staves.agendas, props.reu]);

  useEffect(() => {
    if (state.entities.tableData.length) {
      const valueArray = state.entities.tableData.map(
        (value: { agn: { value: string } }) =>
          value.agn === undefined ? '01' : value.agn.value
      );

      getNucleuses(state.data.campos.cooperative, valueArray)
        .then((nucleou) => {
          changeState(
            RegisterContextActions.ENTITIES_ALL_NUCLEOU_PER_AGENCY,
            valueArray.map((agencies) => {
              return {
                agn: agencies,
                nuc: getNucleusByKey(nucleou.data, String(agencies)).map(
                  (agn) => agn
                ),
              };
            })
          );
        })
        .catch((err) => {
          if (err.code !== 'ERR_CANCELED') {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: translation.Toasts.Errors.loadNucleou,
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.entities.tableData]);

  const [calledOnce, hasCalledOnce] = useState<number>(0);

  useEffect(() => {
    if (calledOnce <= 0 && completedData && Number(match.params.id)) {
      getAgendaRegister(Number(match.params.id))
        .then((table) => {
          hasCalledOnce((e) => e + 1);
          changeState(RegisterContextActions.STAVES_TABLE_DATA, table.data);
          changeState(
            RegisterContextActions.STAVES_INITIAL_TABLE_DATA,
            table.data
          );
        })
        .catch(() => {});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedData, state.stepId]);

  useEffect(() => {
    if (
      state.data.campos.cooperative !== null &&
      state.data.campos.cooperative !== undefined &&
      state.data.campos.cooperative !== ''
    ) {
      if (auth.usuario?.coops!.includes(state.data.campos.cooperative!)) {
        return;
      } else {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: 'Você não possui permissão para acessar está página!',
        });

        setTimeout(() => {
          history.push(AssessorRoutePath);
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.campos.cooperative]);

  useEffect(() => {
    if (
      match.params.id &&
      [(StepStatus.EM_EDICAO, StepStatus.VISUALIZACAO)].includes(state.status)
    ) {
      dispatchAction(
        assembleiasAssessorLoadEventAttachment(
          Number(match.params.id),
          (e: any) => {
            changeState(
              RegisterContextActions.ATTACHMENT_TABLE_DATA,
              e.documents.concat(e.videos)
            );
          }
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.id, state.status]);

  useEffect(() => {
    if (
      ((window.location.pathname ===
        '/plataforma/assembleias/cadastro/etapas/dados' ||
        window.location.pathname ===
          '/plataforma/assembleias/reuniao/cadastro/etapas/dados') &&
        ![
          AuthUserPerfilId.PertencerAdmin,
          AuthUserPerfilId.PertencerGestao,
          AuthUserPerfilId.PertencerConsulta,
        ].includes(perfil!?.id!)) ||
      (state.eventStatus &&
        state.eventStatus === SituationValues.EDITABLE &&
        ![
          AuthUserPerfilId.PertencerAdmin,
          AuthUserPerfilId.PertencerGestao,
          AuthUserPerfilId.PertencerConsulta,
        ].includes(perfil!?.id!)) ||
      state.eventStatus === 'EXPIRED'
    ) {
      setTimeout(() => {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: 'Você não possui permissão para acessar está página!',
        });
      }, 1000);
      history.push(AssessorRoutePath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname, match.params.id, state.eventStatus]);

  useEffect(() => {
    if (
      [AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id) &&
      !props.reu
    ) {
      setStepStatus(StepStatus.VISUALIZACAO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname, match.params.id, state.eventStatus]);

  useEffect(() => {
    const match = location.pathname.match(
      /^\/plataforma\/assembleias\/cadastro\/(\d{5})\/etapas\/(.+)/
    );

    if (match && dataGet?.data.type === AssemblyType.REU) {
      const id = match[1];
      const step = match[2];

      history.push(
        `/plataforma/assembleias/reuniao/cadastro/${id}/etapas/${step}`
      );
    }
  }, [dataGet?.data.type, history, location.pathname]);

  const changeRoute = useCallback(
    (evt: number) => {
      if (
        state.status === StepStatus.EM_EDICAO ||
        state.eventStatus === SituationValues.EDITABLE
      ) {
        switch (match.params.etapa) {
          case 'dados':
            changeState(
              RegisterContextActions.SET_SHOW_ALERT,
              JSON.stringify(state.dataOld.campos) !==
                JSON.stringify(state.data.campos)
            );

            return JSON.stringify(state.dataOld.campos) ===
              JSON.stringify(state.data.campos)
              ? history.push(
                  `${shouldRenderReuOrAssembly}${
                    match.params.id && match.params.etapa
                      ? `${match.params.id}/etapas/${
                          getAssemblySteps(
                            state.data.campos.modality,
                            state.data.campos.type
                          )[evt]
                        }`
                      : `etapas/${StepIds.data}`
                  }`
                )
              : undefined;
          case 'entidade':
            changeState(
              RegisterContextActions.SET_SHOW_ALERT,
              JSON.stringify(state.entitiesOld.tableData) !==
                JSON.stringify(state.entities.tableData)
            );
            return JSON.stringify(state.entitiesOld.tableData) ===
              JSON.stringify(state.entities.tableData)
              ? history.push(
                  `${shouldRenderReuOrAssembly}${
                    match.params.id && match.params.etapa
                      ? `${match.params.id}/etapas/${
                          getAssemblySteps(
                            state.data.campos.modality,
                            state.data.campos.type
                          )[evt]
                        }`
                      : `etapas/${StepIds.data}`
                  }`
                )
              : undefined;
          case 'local':
            changeState(
              RegisterContextActions.SET_SHOW_ALERT,
              JSON.stringify(state.localOld.inputs) !==
                JSON.stringify(state.local.inputs)
            );
            return JSON.stringify(state.localOld.inputs) ===
              JSON.stringify(state.local.inputs)
              ? history.push(
                  `${shouldRenderReuOrAssembly}${
                    match.params.id && match.params.etapa
                      ? `${match.params.id}/etapas/${
                          getAssemblySteps(
                            state.data.campos.modality,
                            state.data.campos.type
                          )[evt]
                        }`
                      : `etapas/${StepIds.data}`
                  }`
                )
              : undefined;
          case 'pesquisa':
            changeState(
              RegisterContextActions.SET_SHOW_ALERT,
              state.survey.enabled !== state.survey.enabledOld ||
                state.survey.template !== state.survey.templateOld
            );
            return state.survey.enabled === state.survey.enabledOld &&
              state.survey.template === state.survey.templateOld
              ? history.push(
                  `${shouldRenderReuOrAssembly}${
                    match.params.id && match.params.etapa
                      ? `${match.params.id}/etapas/${
                          getAssemblySteps(
                            state.data.campos.modality,
                            state.data.campos.type
                          )[evt]
                        }`
                      : `etapas/${StepIds.data}`
                  }`
                )
              : undefined;
          case 'anexo':
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            return history.push(
              `${shouldRenderReuOrAssembly}${
                match.params.id && match.params.etapa
                  ? `${match.params.id}/etapas/${
                      getAssemblySteps(
                        state.data.campos.modality,
                        state.data.campos.type
                      )[evt]
                    }`
                  : `etapas/${StepIds.data}`
              }`
            );
          case 'pauta':
            changeState(
              RegisterContextActions.SET_SHOW_ALERT,
              JSON.stringify(state.staves.agendas) !==
                JSON.stringify(state.staves.agendasOld)
            );

            return JSON.stringify(state.staves.agendas) ===
              JSON.stringify(state.staves.agendasOld)
              ? history.push(
                  `${shouldRenderReuOrAssembly}${
                    match.params.id && match.params.etapa
                      ? `${match.params.id}/etapas/${
                          getAssemblySteps(
                            state.data.campos.modality,
                            state.data.campos.type
                          )[evt]
                        }`
                      : `etapas/${StepIds.data}`
                  }`
                )
              : undefined;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      match.params.etapa,
      match.params.id,
      state.data.campos,
      state.entities.tableData,
      state.local.inputs,
      state.survey.enabled,
      state.survey.enabledOld,
      state.survey.template,
      state.survey.templateOld,
      state.staves.agendas,
      state.staves.agendasOld,
    ]
  );

  const tagTheme = (status: SituationValues) => {
    switch (status) {
      case SituationValues.EDITABLE:
        return styles.edicao;
      case SituationValues.RESERVABLE:
        return styles.reservado;
      case SituationValues.AVAILABLE:
        return styles.disponivel;
      case SituationValues.BLOCKED:
        return styles.bloqueado;
      case SituationValues.IN_PREPARATION:
        return styles.preparacao;
      case SituationValues.IN_ASSEMBLY:
        return styles.assembleia;
      case SituationValues.FINISHED:
        return styles.finalizado;
      case SituationValues.EXPIRED:
        return styles.expirado;
      default:
        return styles.default;
    }
  };

  const tagIcon = (status: SituationValues) => {
    switch (status) {
      case SituationValues.EDITABLE:
        return <EditIcon />;
      case SituationValues.RESERVABLE:
        return <ClockIcon />;
      case SituationValues.AVAILABLE:
        return <AlertIcon />;
      case SituationValues.BLOCKED:
        return <ExclamationIcon />;
      case SituationValues.IN_PREPARATION:
        return <CheckCircleIcon />;
      case SituationValues.IN_ASSEMBLY:
        return <CheckCircleIcon />;
      case SituationValues.FINISHED:
        return <CheckCircleIcon />;
      case SituationValues.EXPIRED:
        return <AlertIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  return (
    <Fragment>
      {loader ? (
        <Loading
          text={'Aguarde, enquanto o evento está sendo carregado.'}
          inline
        />
      ) : undefined}
      <PrivateContainer
        header={
          <Fragment>
            <Col>
              <Title
                showBackButton
                onBackClick={() => history.push(AssessorRoutePath)}
                rightElement={
                  state.eventStatus ? (
                    <div className={styles.etiqueta_container}>
                      <div className={clsx([styles.etiquetas, styles.center])}>
                        <div
                          className={clsx([
                            styles.theme,
                            tagTheme(state.eventStatus!),
                          ])}
                        >
                          {tagIcon(state.eventStatus!)}
                          <span>
                            {
                              SituationText[
                                state.eventStatus! as unknown as keyof typeof SituationText
                              ]
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : undefined
                }
              >
                {translation.Assessor.Evento.Cadastro.Titulo(props.reu)}
              </Title>
            </Col>
            <Col>
              <Breadcrumb itens={breadcrumb} />
            </Col>
          </Fragment>
        }
        className={styles.root}
      >
        <Row>
          <Col>
            <Etapas
              atual={getAssemblySteps(
                state.data.campos.modality,
                state.data.campos.type,
                props.reu
              ).indexOf(match.params.etapa as StepIds)}
              setAtual={(evt: number) => {
                if (
                  state.status === StepStatus.EM_EDICAO ||
                  state.eventStatus === SituationValues.EDITABLE
                ) {
                  changeRoute(evt);
                  changeState(
                    RegisterContextActions.SET_TO_PAGE,
                    `${shouldRenderReuOrAssembly}${
                      match.params.id && match.params.etapa
                        ? `${match.params.id}/etapas/${
                            getAssemblySteps(
                              state.data.campos.modality,
                              state.data.campos.type
                            )[evt]
                          }`
                        : `etapas/${StepIds.data}`
                    }`
                  );
                } else {
                  history.push(
                    `${shouldRenderReuOrAssembly}${
                      match.params.id && match.params.etapa
                        ? `${match.params.id}/etapas/${
                            getAssemblySteps(
                              state.data.campos.modality,
                              state.data.campos.type
                            )[evt]
                          }`
                        : `etapas/${StepIds.data}`
                    }`
                  );
                }
              }}
              scrollOnChange
              etapas={getAssemblySteps(
                state.data.campos.modality,
                state.data.campos.type,
                props.reu
              ).map((etapa) => {
                return {
                  name:
                    (translation.Assessor.Evento.Cadastro.Etapas as any)[
                      capitalize(etapa)
                    ].Nome ?? capitalize(etapa),
                  ...getStepDisabledCompleted(etapa),
                };
              })}
            />
          </Col>
        </Row>
        <TransitionGroup
          className={clsx([styles.etapas, opacity ? styles.opacity : ''])}
        >
          <CSSTransition
            key={history.location.key}
            timeout={500}
            classNames={{
              enter: styles.etapaEnter,
              enterActive: styles.etapaEnterActive,
              enterDone: styles.etapaEnterDone,
              appearDone: styles.etapaEnterDone,
              exit: styles.etapaExit,
              exitActive: styles.etapaExitActive,
              exitDone: styles.etapaExitDone,
            }}
            onEnter={() => setOpacity(false)}
          >
            <Switch location={history.location}>
              <Route
                exact
                path={`${shouldRenderReuOrAssembly}:id?/etapas/dados`}
                render={() => <Data reu={props.reu} />}
              />
              <Route
                exact
                path={`${shouldRenderReuOrAssembly}:id?/etapas/entidade`}
                render={() => <Entities reu={props.reu} />}
              />
              <Route
                exact
                path={`${shouldRenderReuOrAssembly}:id?/etapas/local`}
                render={() => <Local reu={props.reu} />}
              />
              <Route
                exact
                path={`${shouldRenderReuOrAssembly}:id?/etapas/pauta`}
                render={() => <Staves reu={props.reu} />}
              />
              <Route
                exact
                path={`${shouldRenderReuOrAssembly}:id?/etapas/pesquisa`}
                render={() => <Survey />}
              />
              <Route
                exact
                path={`${shouldRenderReuOrAssembly}:id?/etapas/anexo`}
                render={() => <Attachment reu={props.reu} />}
              />
              <Route path={'*'}>
                <Redirect to={`${shouldRenderReuOrAssembly}etapas/dados`} />
              </Route>
            </Switch>
          </CSSTransition>
        </TransitionGroup>
      </PrivateContainer>
    </Fragment>
  );
}
